import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';

import {Translatable, TranslationService} from '@ngmedax/translation';

import {QrCodeService} from '../../services/qr-code.service';
import {MobileLinkService} from '../../services/mobile-link.service';
import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';


// hack to inject decorator declarations. must occur before class declaration!
export interface QrCodeDisplayComponent extends Translatable {}

/**
 * Component for displaying QR codes with mode switching, print and open functionality.
 * Supports both mobile app QR codes and browser link QR codes.
 */
@Component({
  selector: 'app-qr-code-display',
  templateUrl: './qr-code-display.component.html',
  styleUrls: ['./qr-code-display.component.css']
})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class QrCodeDisplayComponent {
  /**
   * Patient UID for QR code generation.
   */
  @Input() patientUid: string;

  /**
   * Locale for QR code data.
   */
  @Input() locale: string = 'de_DE';

  /**
   * Mobile frontend link for browser mode.
   */
  @Input() qrFillLink: string = '';

  /**
   * Whether the form has unsaved changes.
   */
  @Input() dirty: boolean = true;

  /**
   * Whether questionnaires are selected.
   */
  @Input() hasQuestionnaires: boolean = false;

  /**
   * Current QR mode (1 = mobile app, 2 = browser).
   */
  @Input() qrMode: number = 1;

  /**
   * Event emitted when QR mode changes.
   */
  @Output() modeChanged = new EventEmitter<number>();

  /**
   * Injects dependencies.
   */
  public constructor(
    private qrCodeService: QrCodeService,
    private mobileLinkService: MobileLinkService,
    @Optional() private translationService: TranslationService
  ) {
  }

  /**
   * Checks if the QR code should be visible.
   *
   * @returns true if QR code should be shown.
   */
  public get isVisible(): boolean {
    return !!this.patientUid && this.hasQuestionnaires && !this.dirty;
  }

  /**
   * Gets the QR code data for mobile app mode.
   *
   * @returns QR code data string.
   */
  public get mobileQrData(): string {
    return this.mobileLinkService.buildQrCodeData(this.patientUid, this.locale);
  }

  /**
   * Handles QR mode toggle.
   *
   * @param mode The new mode (1 or 2).
   */
  public onToggleMode(mode: number): void {
    this.qrMode = mode;
    this.modeChanged.emit(mode);
  }

  /**
   * Prints the QR code.
   */
  public onPrint(): void {
    this.qrCodeService.print('.qrcode canvas');
  }

  /**
   * Opens the QR code in a new window.
   */
  public onOpenWindow(): void {
    this.qrCodeService.openInNewWindow('.qrcode canvas');
  }
}

